import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, Lock, Mail, Shield, KeyRound } from "lucide-react"
import { authService } from "../services/auth.service";

export function PasswordRecoveryCard({ onBackToLogin }: { onBackToLogin?: () => void }) {
	const navigate = useNavigate();
	const [email, setEmail] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [success, setSuccess] = useState(false);
	const [attempts, setAttempts] = useState(0);

	const validateEmail = (email: string) => {
		const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
		return emailRegex.test(email) && email.length <= 254 && email.length >= 5;
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (!email.trim()) {
			setError("Por favor, insira seu e-mail");
			return;
		}

		if (!validateEmail(email)) {
			setError("Por favor, insira um e-mail válido");
			return;
		}

		if (attempts >= 3) {
			setError("Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.");
			return;
		}

		setError("");
		setLoading(true);

		try {
			await authService.forgotPassword(email);
			setSuccess(true);
			setTimeout(() => {
				navigate("/reset", { state: { email } });
			}, 2000);
		} catch (err: any) {
			setAttempts(prev => prev + 1);
			setError(err.message || "Erro ao enviar email de recuperação");
		} finally {
			setLoading(false);
		}
	};

	if (success) {
		return (
			<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, var(--background) 0%, var(--muted) 100%)', padding: 16 }}>
				<div style={{ width: '100%', maxWidth: 900, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', borderRadius: 20, overflow: 'hidden', background: 'var(--card)', animation: 'slideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)', border: '1px solid var(--border)' }}>
					<div style={{ display: 'flex', flexDirection: 'row', minHeight: 650 }}>
						<div style={{ flex: 3, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', padding: 48, overflow: 'hidden' }}>
							<div style={{ textAlign: 'center', color: 'white', position: 'relative', zIndex: 1 }}>
								<div style={{ marginBottom: 40 }}>
									<div style={{ width: 120, height: 120, margin: '0 auto 32px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', animation: 'checkmarkPulse 2s ease-in-out infinite' }}>
										<Mail style={{ width: 60, height: 60 }} />
									</div>
								</div>
								<h1 style={{ fontSize: 36, fontWeight: 800, marginBottom: 20, letterSpacing: '-1px' }}>Email Enviado!</h1>
								<p style={{ fontSize: 18, opacity: 0.9, lineHeight: 1.6, maxWidth: 400, margin: '0 auto' }}>
									Enviamos um código de verificação para o seu email. Você será redirecionado em breve.
								</p>
							</div>
							<div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, background: 'rgba(255,255,255,0.1)', borderRadius: '50%', animation: 'pulse 4s ease-in-out infinite' }}></div>
							<div style={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, background: 'rgba(255,255,255,0.1)', borderRadius: '50%', animation: 'pulse 4s ease-in-out infinite 1s' }}></div>
						</div>
						<div style={{ flex: 2, background: 'var(--card)' }}>
							<div style={{ padding: 48, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
								<div style={{ textAlign: 'center' }}>
									<h2 style={{ fontSize: 28, fontWeight: 800, color: 'var(--card-foreground)', marginBottom: 16, letterSpacing: '-0.5px' }}>Verifique seu email</h2>
									<p style={{ color: 'var(--muted-foreground)', marginBottom: 28, fontSize: 15, lineHeight: 1.6 }}>
										Enviamos um código de 6 dígitos para:<br/><strong style={{ color: 'var(--card-foreground)', fontSize: 16 }}>{email}</strong>
									</p>
									<div style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)', padding: '16px 24px', borderRadius: 12, border: '1px solid rgba(16, 185, 129, 0.2)' }}>
										<p style={{ color: 'var(--muted-foreground)', fontSize: 14 }}>
											⏳ Redirecionando para a tela de redefinição de senha...
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, var(--background) 0%, var(--muted) 100%)', padding: 16 }}>
			<div style={{ width: '100%', maxWidth: 900, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', borderRadius: 20, overflow: 'hidden', background: 'var(--card)', animation: 'slideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)', border: '1px solid var(--border)' }}>
				<div style={{ display: 'flex', flexDirection: 'row', minHeight: 650 }}>
					<div style={{ flex: 3, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', padding: 48, overflow: 'hidden' }}>
						<div style={{ textAlign: 'center', color: 'white', position: 'relative', zIndex: 1 }}>
							<div style={{ marginBottom: 40 }}>
								<div style={{ width: 120, height: 120, margin: '0 auto 32px', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', animation: 'float 3s ease-in-out infinite' }}>
									<KeyRound style={{ width: 60, height: 60 }} strokeWidth={2} />
								</div>
							</div>
							<h1 style={{ fontSize: 36, fontWeight: 800, marginBottom: 20, letterSpacing: '-1px' }}>Recuperação de Conta</h1>
							<p style={{ fontSize: 18, opacity: 0.9, lineHeight: 1.6, maxWidth: 400, margin: '0 auto' }}>
								Não se preocupe! Vamos ajudá-lo a recuperar o acesso à sua conta de forma segura.
							</p>
						</div>
						<div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, background: 'rgba(255,255,255,0.1)', borderRadius: '50%', animation: 'pulse 4s ease-in-out infinite' }}></div>
						<div style={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, background: 'rgba(255,255,255,0.1)', borderRadius: '50%', animation: 'pulse 4s ease-in-out infinite 1s' }}></div>
					</div>
					<div style={{ flex: 2, background: 'var(--card)' }}>
						<div style={{ padding: 48, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
							<div style={{ marginBottom: 40 }}>
								<h2 style={{ fontSize: 28, fontWeight: 800, color: 'var(--card-foreground)', marginBottom: 12, letterSpacing: '-0.5px' }}>Recuperar Senha</h2>
								<p style={{ color: 'var(--muted-foreground)', fontSize: 15 }}>
									Insira o seu e-mail para que possamos enviar um código de verificação
								</p>
							</div>
							<form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
								<div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
									<label htmlFor="email" style={{ color: 'var(--card-foreground)', fontWeight: 600, fontSize: 14 }}>E-mail</label>
									<input
										id="email"
										type="email"
										value={email}
										onChange={e => setEmail(e.target.value)}
										style={{ background: 'var(--input)', border: '2px solid var(--border)', borderRadius: 10, padding: '14px 16px', fontSize: 15, transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', outline: 'none' }}
										placeholder="seu@email.com"
										autoComplete="username"
										required
										onFocus={(e) => {
											e.currentTarget.style.borderColor = 'var(--primary)';
											e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
										}}
										onBlur={(e) => {
											e.currentTarget.style.borderColor = 'var(--border)';
											e.currentTarget.style.boxShadow = 'none';
										}}
									/>
								</div>
								{error && <div style={{ color: '#ef4444', fontSize: 14, fontWeight: 500, background: 'rgba(239, 68, 68, 0.1)', padding: '12px 16px', borderRadius: 8, border: '1px solid rgba(239, 68, 68, 0.2)' }}>{error}</div>}
								<button
									type="submit"
									disabled={loading}
									style={{
										width: '100%',
										background: loading ? 'var(--muted)' : 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
										color: 'white',
										fontWeight: 700,
										padding: '14px 24px',
										border: 'none',
										borderRadius: 10,
										fontSize: 16,
										cursor: loading ? 'not-allowed' : 'pointer',
										boxShadow: loading ? 'none' : '0 4px 14px rgba(59, 130, 246, 0.4)',
										transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
									}}
									onMouseEnter={(e) => {
										if (!loading) {
											e.currentTarget.style.transform = 'translateY(-2px)';
											e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.5)';
										}
									}}
									onMouseLeave={(e) => {
										if (!loading) {
											e.currentTarget.style.transform = 'translateY(0)';
											e.currentTarget.style.boxShadow = '0 4px 14px rgba(59, 130, 246, 0.4)';
										}
									}}
								>
									{loading ? "Enviando..." : "Continuar"}
								</button>
							</form>
							<div style={{ textAlign: 'center', fontSize: 14, color: 'var(--muted-foreground)', marginTop: 32 }}>
								Lembrou da senha?{' '}
								<button
									style={{ color: 'var(--primary)', background: 'none', border: 'none', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
									type="button"
									onClick={() => {
										if (onBackToLogin) {
											onBackToLogin();
										} else {
											navigate("/login");
										}
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.textDecoration = 'underline';
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.textDecoration = 'none';
									}}
								>
									Voltar ao login
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<style>
				{`
					@keyframes slideUp {
						from {
							opacity: 0;
							transform: translateY(30px);
						}
						to {
							opacity: 1;
							transform: translateY(0);
						}
					}

					@keyframes float {
						0%, 100% {
							transform: translateY(0);
						}
						50% {
							transform: translateY(-20px);
						}
					}

					@keyframes pulse {
						0%, 100% {
							transform: scale(1);
							opacity: 0.1;
						}
						50% {
							transform: scale(1.1);
							opacity: 0.15;
						}
					}

					@keyframes checkmarkPulse {
						0%, 100% {
							transform: scale(1);
						}
						50% {
							transform: scale(1.05);
						}
					}
				`}
			</style>
		</div>
	);
}
